import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ShowCard from "@/components/base/ShowCard";
import { getRelatedShows, getShowById, formatPopularity } from "@/utils/tv";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const FAVORITE_KEY = "shaosong:favorites";

function readFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAVORITE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function Detail() {
  const { id = "" } = useParams();
  const show = getShowById(id);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [toast, setToast] = useState("");

  useEffect(() => {
    setFavorites(readFavorites());
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2600);
    return () => clearTimeout(timer);
  }, [toast]);

  useDocumentMeta({
    title: show ? `${show.title} - 绍宋影视` : "内容不存在 - 绍宋影视",
    description: show
      ? `${show.title}是一部${show.region}${show.genres.join("、")}作品，由${show.director}执导，${show.actors
          .slice(0, 3)
          .join("、")}主演。${show.synopsis.slice(0, 60)}…`
      : "抱歉，你访问的影视内容不存在。",
    keywords: show ? `${show.title},${show.genres.join(",")},绍宋影视,剧情介绍` : "绍宋影视",
  });

  const isFavorite = show ? favorites.includes(show.id) : false;

  const toggleFavorite = () => {
    if (!show) return;
    const next = isFavorite ? favorites.filter((f) => f !== show.id) : [...favorites, show.id];
    setFavorites(next);
    try {
      localStorage.setItem(FAVORITE_KEY, JSON.stringify(next));
    } catch {
      /* ignore storage errors */
    }
    setToast(isFavorite ? "已取消收藏" : "已加入收藏（仅当前设备浏览器有效）");
  };

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("复制失败，请手动复制地址栏链接");
    }
  };

  if (!show) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4 pt-16">
          <div className="text-center max-w-md">
            <span className="inline-flex w-16 h-16 rounded-full bg-background-100 items-center justify-center mb-4">
              <i className="ri-film-line text-3xl text-foreground-400" />
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-900">内容不存在</h1>
            <p className="mt-2 text-sm text-foreground-500">
              抱歉，你访问的影视内容不存在或已下线，可能链接有误。
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line text-lg w-5 h-5 flex items-center justify-center" />
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedShows(show, 6);
  const categoryHref = show.type === "movie" ? "/category/movie" : "/category/tvshow";

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 pt-16">
        <section className="relative overflow-hidden bg-primary-950">
          <img
            src={show.cover}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-top scale-110 blur-2xl opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/85 to-primary-900/70" />

          <div className="relative w-full px-4 md:px-8 pt-8 pb-10">
            <nav className="flex flex-wrap items-center gap-1.5 text-xs text-background-200/80 mb-6" aria-label="面包屑">
              <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-base w-4 h-4 flex items-center justify-center" />
              <Link to={categoryHref} className="hover:text-accent-400 transition-colors">
                {show.type === "movie" ? "电影" : "电视剧"}
              </Link>
              <i className="ri-arrow-right-s-line text-base w-4 h-4 flex items-center justify-center" />
              <span className="text-background-50">{show.title}</span>
            </nav>

            <div className="flex flex-col sm:flex-row gap-6 md:gap-8 items-start">
              <div className="w-[150px] md:w-[210px] shrink-0 aspect-[2/3] rounded-lg overflow-hidden bg-primary-900">
                <img
                  src={show.cover}
                  alt={`${show.title}封面`}
                  title={`${show.title} - 绍宋影视`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-2.5 py-1 text-xs rounded-full bg-accent-500 text-primary-950 font-medium">
                    {show.type === "movie" ? "电影" : "电视剧"}
                  </span>
                  {show.genres.map((g) => (
                    <span key={g} className="px-2.5 py-1 text-xs rounded-full bg-background-50/10 text-background-100 border border-background-50/20">
                      {g}
                    </span>
                  ))}
                </div>

                <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
                  {show.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-background-200">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-star-smile-line text-base w-4 h-4 flex items-center justify-center text-accent-400" />
                    {show.rating === null ? "暂无评分" : show.rating.toFixed(1)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-calendar-line text-base w-4 h-4 flex items-center justify-center" />
                    {show.year} 年
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-earth-line text-base w-4 h-4 flex items-center justify-center" />
                    {show.region}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-fire-line text-base w-4 h-4 flex items-center justify-center" />
                    热度 {formatPopularity(show.popularity)}
                  </span>
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={toggleFavorite}
                    className={`inline-flex items-center gap-2 h-11 px-6 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      isFavorite
                        ? "bg-accent-500 text-primary-950 hover:bg-accent-400"
                        : "bg-primary-500 text-background-50 hover:bg-primary-600"
                    }`}
                  >
                    <i className={`${isFavorite ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg w-5 h-5 flex items-center justify-center`} />
                    {isFavorite ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={share}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/10 border border-background-50/25 text-background-50 text-sm font-medium hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-share-forward-line text-lg w-5 h-5 flex items-center justify-center" />
                    分享链接
                  </button>
                  <Link
                    to={categoryHref}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/10 border border-background-50/25 text-background-50 text-sm font-medium hover:bg-background-50/20 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line text-lg w-5 h-5 flex items-center justify-center" />
                    返回{show.type === "movie" ? "电影" : "电视剧"}
                  </Link>
                </div>

                <p className="mt-4 text-xs text-background-200/70">
                  收藏功能为本地演示，状态仅保存在当前设备浏览器中，不会跨设备同步。
                </p>
              </div>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            <div className="lg:col-span-2">
              <section className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">剧情简介</h2>
                <p className="text-sm md:text-base text-foreground-700 leading-8">{show.synopsis}</p>
              </section>

              <section className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">上映信息</h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-background-200 border border-background-200 rounded-lg overflow-hidden">
                  {[
                    { label: "上映日期", value: show.releaseDate },
                    { label: "年份", value: `${show.year} 年` },
                    { label: "类型", value: show.genres.join(" / ") },
                    { label: "地区", value: show.region },
                    { label: "导演", value: show.director },
                    { label: "评分", value: show.rating === null ? "暂无评分" : show.rating.toFixed(1) },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 bg-background-50 px-4 py-3">
                      <dt className="text-xs text-foreground-500 w-16 shrink-0">{item.label}</dt>
                      <dd className="text-sm text-foreground-900">{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">主演阵容</h2>
                <ul className="flex flex-wrap gap-2">
                  {show.actors.map((actor) => (
                    <li
                      key={actor}
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-secondary-100 text-secondary-900 text-sm"
                    >
                      <i className="ri-user-3-line text-base w-4 h-4 flex items-center justify-center" />
                      {actor}
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-20 bg-background-100 border border-background-200 rounded-lg p-5">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-3">内容说明</h3>
                <p className="text-xs text-foreground-600 leading-6 mb-4">
                  本站为影视内容浏览演示平台，页面仅展示作品基本信息、剧情介绍与相关推荐，不提供视频播放、下载或付费服务。
                </p>
                <ul className="space-y-2.5 text-xs text-foreground-600">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-base w-4 h-4 flex items-center justify-center text-accent-500 mt-0.5" />
                    所有信息均为演示数据
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-base w-4 h-4 flex items-center justify-center text-accent-500 mt-0.5" />
                    封面与详情内容一一对应
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-base w-4 h-4 flex items-center justify-center text-accent-500 mt-0.5" />
                    收藏状态保存在本地浏览器
                  </li>
                </ul>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-12 pt-10 border-t border-background-200">
              <div className="flex items-end justify-between gap-4 mb-6">
                <div>
                  <span className="inline-flex items-center gap-2 text-xs font-medium text-accent-600 uppercase tracking-widest mb-2">
                    <span className="block w-6 h-1 rounded-full bg-accent-500" />
                    猜你喜欢
                  </span>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">相关推荐</h2>
                </div>
                <Link
                  to={categoryHref}
                  className="hidden sm:inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 transition-colors whitespace-nowrap shrink-0"
                >
                  更多同类
                  <i className="ri-arrow-right-s-line text-lg w-5 h-5 flex items-center justify-center" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 md:gap-5">
                {related.map((item) => (
                  <ShowCard key={item.id} show={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-fade-up">
          <div className="flex items-center gap-2 px-4 py-3 rounded-md bg-foreground-950 text-background-50 text-sm">
            <i className="ri-information-line text-base w-4 h-4 flex items-center justify-center text-accent-400" />
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}